// PDF Export Service using browser print API
// Generates clean, printable reports from dashboard data

import { generateExecutiveUSSoccerPDF } from './usSoccerPdfTemplate';
import { generateExecutiveNationalTeamPDF } from './nationalTeamPdfTemplate';
import { generateBrandIntelligenceReportPDF, getBrandIntelligenceFileName } from './brandIntelligenceReportTemplate';

// Direct PDF download using jsPDF — builds PDF programmatically, instant generation
async function downloadHTMLAsPDF(htmlContent, fileName) {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = 210, H = 297, M = 16;
  const CW = W - M * 2;
  let y = M;

  // Parse HTML to extract structured content
  const tmp = document.createElement('div');
  tmp.innerHTML = htmlContent;

  // Helpers
  const pageCheck = (needed) => { if (y + needed > H - 20) { pdf.addPage(); y = M; } };
  const drawBar = (x, bY, w, score) => {
    const c = score >= 80 ? [16,185,129] : score >= 60 ? [59,130,246] : score >= 40 ? [245,158,11] : [239,68,68];
    pdf.setFillColor(229, 231, 235); pdf.rect(x, bY, w, 3, 'F');
    pdf.setFillColor(...c); pdf.rect(x, bY, w * score / 100, 3, 'F');
    return c;
  };

  // ═══ COVER ═══
  pdf.setFillColor(15, 23, 42); pdf.rect(0, 0, W, 12, 'F');
  pdf.setFontSize(8); pdf.setTextColor(255, 255, 255); pdf.setFont('helvetica', 'bold');
  pdf.text('YOUR BRAND VALUE SPORTS', M, 8);
  const confText = (tmp.querySelector('.conf') || {}).textContent || 'CONFIDENTIAL';
  pdf.setFontSize(6); pdf.setFont('helvetica', 'normal');
  pdf.text(confText.trim(), W - M, 8, { align: 'right' });

  y = 85;
  pdf.setFontSize(7); pdf.setTextColor(59, 130, 246); pdf.setFont('helvetica', 'bold');
  pdf.text('BRAND INTELLIGENCE', W / 2, y, { align: 'center' }); y += 12;

  const title = (tmp.querySelector('.cover h1') || {}).textContent || 'BRAND INTELLIGENCE REPORT';
  pdf.setFontSize(22); pdf.setTextColor(15, 23, 42);
  pdf.text(title.trim(), W / 2, y, { align: 'center' }); y += 9;

  const name = (tmp.querySelector('.cover .name') || {}).textContent || '';
  pdf.setFontSize(18); pdf.setTextColor(59, 130, 246);
  pdf.text(name.trim(), W / 2, y, { align: 'center' }); y += 7;

  const sub = (tmp.querySelector('.cover .sub') || {}).textContent || '';
  pdf.setFontSize(10); pdf.setTextColor(100, 116, 139); pdf.setFont('helvetica', 'normal');
  pdf.text(sub.trim(), W / 2, y, { align: 'center' }); y += 5;

  const tagline = (tmp.querySelector('.cover .tagline') || {}).textContent || '';
  pdf.setFontSize(8); pdf.setTextColor(148, 163, 184);
  pdf.text(tagline.trim(), W / 2, y, { align: 'center' }); y += 14;

  // Cover meta table
  tmp.querySelectorAll('.cover .meta-table tr').forEach(row => {
    const cells = row.querySelectorAll('td');
    if (cells.length >= 2) {
      pdf.setFontSize(7.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(100, 116, 139);
      pdf.text(cells[0].textContent.trim(), W / 2 - 45, y);
      pdf.setFont('helvetica', 'normal'); pdf.setTextColor(15, 23, 42);
      pdf.text(cells[1].textContent.trim(), W / 2 + 12, y);
      y += 5;
    }
  });

  // ═══ SECTIONS ═══
  pdf.addPage(); y = M;

  tmp.querySelectorAll('.section').forEach(section => {
    const titleEl = section.querySelector('.section-title');
    if (!titleEl) return;

    pageCheck(18);
    pdf.setFontSize(12); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(15, 23, 42);
    pdf.text(titleEl.textContent.trim(), M, y); y += 1.5;
    pdf.setDrawColor(59, 130, 246); pdf.setLineWidth(0.5);
    pdf.line(M, y, M + Math.min(pdf.getTextWidth(titleEl.textContent.trim()), CW), y);
    pdf.setLineWidth(0.2); y += 5;

    // Score hero block
    const hero = section.querySelector('.score-hero');
    if (hero) {
      pageCheck(28);
      pdf.setFillColor(248, 250, 252);
      pdf.roundedRect(M, y - 2, CW, 22, 2, 2, 'F');
      const sn = (hero.querySelector('.number') || {}).textContent || '';
      const sl = (hero.querySelector('.level') || {}).textContent || '';
      pdf.setFontSize(28); pdf.setTextColor(59, 130, 246); pdf.setFont('helvetica', 'bold');
      pdf.text(sn.trim(), W / 2, y + 8, { align: 'center' });
      pdf.setFontSize(7); pdf.setTextColor(100, 116, 139); pdf.setFont('helvetica', 'normal');
      pdf.text(sl.trim(), W / 2, y + 15, { align: 'center' });
      y += 26;
    }

    // H3 subheadings
    section.querySelectorAll('h3').forEach(h3 => {
      if (h3.closest('.score-hero')) return;
      pageCheck(8);
      pdf.setFontSize(9); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(15, 23, 42);
      pdf.text(h3.textContent.trim(), M, y); y += 5;
    });

    // Dimension bars
    section.querySelectorAll('.dim-row').forEach(row => {
      const label = (row.querySelector('.dim-label') || {}).textContent || '';
      const spans = row.querySelectorAll('span');
      let scoreVal = 0;
      spans.forEach(s => { const n = parseInt(s.textContent); if (!isNaN(n) && n <= 100) scoreVal = n; });
      pageCheck(7);
      pdf.setFontSize(7.5); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(71, 85, 105);
      pdf.text(label.trim(), M, y);
      const c = drawBar(M + 62, y - 2.5, 65, scoreVal);
      pdf.setFontSize(7.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...c);
      pdf.text(String(scoreVal), M + 130, y);
      // Extra label (level text) if present
      const extras = row.querySelectorAll('div');
      const last = extras[extras.length - 1];
      if (last && last.textContent.trim() !== label.trim() && !last.querySelector('.dim-label') && !last.querySelector('span')) {
        pdf.setFontSize(6.5); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(100, 116, 139);
        pdf.text(last.textContent.trim().substring(0, 25), M + 140, y);
      }
      y += 6;
    });

    // Findings
    section.querySelectorAll('.finding').forEach((f, i) => {
      const text = (f.querySelector('.finding-text') || {}).textContent || '';
      if (!text) return;
      const numEl = f.querySelector('.finding-num');
      const st = numEl?.getAttribute('style') || '';
      const cc = st.includes('#10b981') ? [16,185,129] : st.includes('#f59e0b') ? [245,158,11] : [59,130,246];
      pageCheck(12);
      pdf.setFillColor(...cc);
      pdf.circle(M + 3, y - 1, 2.5, 'F');
      pdf.setFontSize(5.5); pdf.setTextColor(255, 255, 255); pdf.setFont('helvetica', 'bold');
      pdf.text(numEl?.textContent.trim() || String(i + 1), M + 3, y, { align: 'center' });
      pdf.setFontSize(7.5); pdf.setTextColor(51, 65, 85); pdf.setFont('helvetica', 'normal');
      const lines = pdf.splitTextToSize(text, CW - 12);
      lines.forEach(l => { if (y > H - 20) { pdf.addPage(); y = M; } pdf.text(l, M + 9, y); y += 3.2; });
      y += 1.5;
    });

    // Recommendations
    section.querySelectorAll('.rec-item').forEach((r, i) => {
      const text = (r.querySelector('.rec-text') || {}).textContent || '';
      if (!text) return;
      pageCheck(12);
      pdf.setFillColor(59, 130, 246);
      pdf.circle(M + 3, y - 1, 2.5, 'F');
      pdf.setFontSize(5.5); pdf.setTextColor(255, 255, 255); pdf.setFont('helvetica', 'bold');
      pdf.text(String(i + 1), M + 3, y, { align: 'center' });
      pdf.setFontSize(7.5); pdf.setTextColor(51, 65, 85); pdf.setFont('helvetica', 'normal');
      const lines = pdf.splitTextToSize(text, CW - 12);
      lines.forEach(l => { if (y > H - 20) { pdf.addPage(); y = M; } pdf.text(l, M + 9, y); y += 3.2; });
      y += 1.5;
    });

    // Data tables
    section.querySelectorAll('.data-table').forEach(table => {
      table.querySelectorAll('tr').forEach(row => {
        const cells = row.querySelectorAll('th, td');
        if (!cells.length) return;
        pageCheck(5);
        let x = M;
        const colW = CW / Math.max(cells.length, 1);
        cells.forEach(cell => {
          const isH = cell.tagName === 'TH';
          pdf.setFontSize(isH ? 6.5 : 7.5);
          pdf.setFont('helvetica', isH ? 'bold' : 'normal');
          pdf.setTextColor(isH ? 100 : 51, isH ? 116 : 65, isH ? 139 : 85);
          pdf.text(cell.textContent.trim().substring(0, 35), x, y);
          x += colW;
        });
        y += cells[0]?.tagName === 'TH' ? 4 : 3.8;
      });
      y += 3;
    });

    // Meta tables within section
    section.querySelectorAll(':scope > .meta-table').forEach(table => {
      table.querySelectorAll('tr').forEach(row => {
        const cells = row.querySelectorAll('td');
        if (cells.length >= 2) {
          pageCheck(5);
          pdf.setFontSize(7); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(100, 116, 139);
          pdf.text(cells[0].textContent.trim(), M, y);
          pdf.setFont('helvetica', 'normal'); pdf.setTextColor(15, 23, 42);
          const val = cells[1].textContent.trim();
          const vl = pdf.splitTextToSize(val, CW - 50);
          vl.forEach((l, li) => pdf.text(l, M + 48, y + li * 3.2));
          y += Math.max(4.5, vl.length * 3.2 + 1);
        }
      });
      y += 2;
    });

    // Methodology / disclaimer boxes
    section.querySelectorAll('.methodology-box, .disclaimer-box').forEach(box => {
      const text = box.textContent.trim();
      const isDisc = box.classList.contains('disclaimer-box');
      const lines = pdf.splitTextToSize(text, CW - 8);
      const bH = lines.length * 3 + 6;
      pageCheck(bH + 4);
      pdf.setFillColor(isDisc ? 255 : 248, isDisc ? 251 : 250, isDisc ? 235 : 252);
      pdf.roundedRect(M, y - 1, CW, bH, 2, 2, 'F');
      pdf.setFontSize(7); pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(isDisc ? 146 : 100, isDisc ? 64 : 116, isDisc ? 14 : 139);
      lines.forEach(l => { pdf.text(l, M + 4, y + 2); y += 3; });
      y += 6;
    });

    // Paragraphs (exec summary etc.)
    if (!hero) {
      section.querySelectorAll(':scope > div > p').forEach(p => {
        const text = p.textContent.trim();
        if (!text || text.length < 10) return;
        pageCheck(10);
        pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(51, 65, 85);
        const lines = pdf.splitTextToSize(text, CW);
        lines.forEach(l => { if (y > H - 20) { pdf.addPage(); y = M; } pdf.text(l, M, y); y += 3.3; });
        y += 2;
      });
    }

    y += 5;
  });

  // ═══ FOOTER ═══
  const pages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    pdf.setPage(i);
    pdf.setDrawColor(229, 231, 235); pdf.line(M, H - 14, W - M, H - 14);
    pdf.setFontSize(5.5); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(148, 163, 184);
    pdf.text('Your Brand Value Sports \u00b7 sports.yourbrandvalue.com \u00b7 info@yourbrandvalue.com', W / 2, H - 10, { align: 'center' });
    pdf.text(`${i} / ${pages}`, W - M, H - 10, { align: 'right' });
  }

  pdf.save(fileName);
}

// Tenant label map — must match backend TENANT_REGISTRY
const TENANT_LABELS = {
  'us-soccer': 'US SOCCER',
  'demo-tenant': 'DEMO',
};

function resolveTenantLabel() {
  const seg = window.location.pathname.split('/')[1] || '';
  return TENANT_LABELS[seg] || seg.toUpperCase().replace(/-/g, ' ') || 'YBV';
}

export const generatePDFReport = async (data, type = 'national-team', options = {}) => {

  // ── Brand Intelligence: direct PDF download ──
  if (type === 'brand-intelligence') {
    const lang = options.lang || 'en';
    const htmlContent = generateBrandIntelligenceReportPDF(lang, {
      profile: options.profile,
      intelligenceData: options.intelligenceData,
    });
    const fileName = getBrandIntelligenceFileName(lang);
    await downloadHTMLAsPDF(htmlContent, fileName);
    return;
  }

  // ── All other report types: popup + print ──
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  
  if (!printWindow) {
    alert('Please allow pop-ups to generate PDF reports');
    return;
  }
  
  // Resolve tenant label for watermark from URL context
  const tenantLabel = (options.tenantLabel || resolveTenantLabel()).toUpperCase();
  
  let htmlContent = '';
  
  if (type === 'national-team') {
    const teamId = options.teamId || data?.id || '';
    if (teamId === 'usmnt' || teamId === 'uswnt') {
      htmlContent = generateExecutiveNationalTeamPDF(
        data, 
        teamId, 
        options.verifiedSocialData || null, 
        options.isDemoMode !== undefined ? options.isDemoMode : true
      );
    } else {
      htmlContent = generateNationalTeamReport(data);
    }
  } else if (type === 'athlete') {
    htmlContent = generateAthleteReport(data);
  } else if (type === 'us-soccer') {
    htmlContent = generateExecutiveUSSoccerPDF(data);
  } else if (type === 'executive-dashboard') {
    htmlContent = generateExecutiveDashboardPDF(data, options);
  }
  
  // Tenant-scope watermarks — replace hardcoded "US SOCCER PILOT" with active tenant
  htmlContent = htmlContent.replace(/US SOCCER PILOT/g, `${tenantLabel} PILOT`);
  
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  
  // Wait for content to load, then trigger print
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };
};

// Executive Dashboard PDF Template - Board-Level Strategic Intelligence
const generateExecutiveDashboardPDF = (data, options = {}) => {
  const brand = data?.brand_metrics || {};
  const eco = data?.economic_value || {};
  const digital = data?.digital_presence || {};
  const roster = data?.roster_brand_signals || {};
  
  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(0)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
  };

  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Executive Strategic Dashboard - U.S. Soccer Federation</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          color: #1a1a2e;
          line-height: 1.6;
          padding: 40px;
          max-width: 850px;
          margin: 0 auto;
          font-size: 11px;
          position: relative;
        }
        .pdf-watermark {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(-35deg);
          font-size: 48px;
          font-weight: 800;
          color: rgba(220, 38, 38, 0.06);
          white-space: nowrap;
          pointer-events: none;
          z-index: 0;
          letter-spacing: 4px;
        }
        .pdf-watermark-footer {
          text-align: center;
          font-size: 9px;
          color: #999;
          border-top: 1px solid #e5e7eb;
          padding-top: 12px;
          margin-top: 30px;
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          border-bottom: 3px solid #1a1a2e;
          padding-bottom: 20px;
          margin-bottom: 25px;
        }
        .logo { font-size: 20px; font-weight: bold; color: #22c55e; }
        .logo span { color: #1a1a2e; }
        .confidential { 
          background: #fee2e2; 
          color: #dc2626; 
          padding: 4px 10px; 
          border-radius: 4px;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.5px;
        }
        .title-section { text-align: right; }
        .main-title { font-size: 22px; font-weight: bold; margin-bottom: 4px; }
        .subtitle { font-size: 12px; color: #666; }
        .meta-bar {
          background: #f8f9fa;
          padding: 12px 16px;
          border-radius: 6px;
          margin-bottom: 25px;
          display: flex;
          gap: 30px;
          font-size: 10px;
        }
        .meta-item span { color: #666; }
        .meta-item strong { color: #1a1a2e; }
        .section { margin-bottom: 25px; page-break-inside: avoid; }
        .section-title { 
          font-size: 14px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #1a1a2e;
          margin-bottom: 12px;
          padding-bottom: 6px;
          border-bottom: 2px solid #e5e5e5;
        }
        .kpi-grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: 10px; }
        .kpi-card {
          background: #f8f9fa;
          padding: 12px;
          border-radius: 6px;
          text-align: center;
          border: 1px solid #e5e5e5;
        }
        .kpi-card.highlight { background: #ecfdf5; border-color: #22c55e; grid-column: span 2; }
        .kpi-value { font-size: 18px; font-weight: 700; color: #1a1a2e; }
        .kpi-value.positive { color: #22c55e; }
        .kpi-value.negative { color: #dc2626; }
        .kpi-label { font-size: 9px; color: #666; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 4px; }
        .kpi-sublabel { font-size: 8px; color: #999; margin-top: 2px; }
        .risk-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; }
        .risk-card {
          padding: 15px;
          border-radius: 8px;
          border: 1px solid #e5e5e5;
        }
        .risk-card.high { background: #fef2f2; border-color: #fca5a5; }
        .risk-card.medium { background: #fffbeb; border-color: #fcd34d; }
        .risk-card.low { background: #ecfdf5; border-color: #6ee7b7; }
        .risk-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
        .risk-title { font-weight: 600; font-size: 11px; }
        .risk-badge { 
          padding: 2px 8px; 
          border-radius: 10px; 
          font-size: 9px; 
          font-weight: 600;
        }
        .risk-badge.high { background: #dc2626; color: white; }
        .risk-badge.medium { background: #f59e0b; color: white; }
        .risk-badge.low { background: #22c55e; color: white; }
        .risk-impact { font-size: 16px; font-weight: 700; margin-bottom: 6px; }
        .risk-impact.negative { color: #dc2626; }
        .risk-description { font-size: 10px; color: #666; line-height: 1.5; margin-bottom: 10px; }
        .risk-recommendation { 
          font-size: 9px; 
          padding: 8px; 
          background: rgba(255,255,255,0.7); 
          border-radius: 4px;
        }
        .risk-recommendation strong { display: block; margin-bottom: 3px; color: #666; text-transform: uppercase; }
        .opp-card { background: #f0fdf4; border-color: #86efac; }
        .opp-impact { color: #22c55e; }
        .projection-table { width: 100%; border-collapse: collapse; margin-top: 10px; }
        .projection-table th {
          background: #f1f5f9;
          padding: 10px;
          text-align: left;
          font-size: 9px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 2px solid #e2e8f0;
        }
        .projection-table td {
          padding: 10px;
          border-bottom: 1px solid #e5e5e5;
          font-size: 10px;
        }
        .projection-table .impact-positive { color: #22c55e; font-weight: 700; }
        .projection-table .impact-negative { color: #dc2626; font-weight: 700; }
        .projection-table .impact-neutral { color: #f59e0b; font-weight: 700; }
        .disclaimer {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 6px;
          font-size: 9px;
          color: #666;
          line-height: 1.6;
          margin-top: 25px;
        }
        .disclaimer strong { color: #1a1a2e; }
        .footer {
          margin-top: 20px;
          padding-top: 15px;
          border-top: 1px solid #e5e5e5;
          display: flex;
          justify-content: space-between;
          font-size: 9px;
          color: #999;
        }
        @media print {
          body { padding: 20px; }
          .section { page-break-inside: avoid; }
        }
      </style>
    </head>
    <body>
      <div class="pdf-watermark">CONFIDENTIAL — US SOCCER PILOT</div>
      <div class="header">
        <div>
          <div class="logo"><span>YBV</span> Brand Intelligence</div>
          <div class="confidential">CONFIDENTIAL</div>
        </div>
        <div class="title-section">
          <div class="main-title">Executive Strategic Dashboard</div>
          <div class="subtitle">U.S. Soccer Federation | USMNT</div>
        </div>
      </div>

      <div class="meta-bar">
        <div class="meta-item"><span>Assessment Date:</span> <strong>${dateStr}</strong></div>
        <div class="meta-item"><span>Data Source:</span> <strong>YBV Brand Intelligence Platform</strong></div>
        <div class="meta-item"><span>Classification:</span> <strong>Internal Use Only</strong></div>
      </div>

      <div class="section">
        <div class="section-title">Executive Snapshot</div>
        <div class="kpi-grid">
          <div class="kpi-card highlight">
            <div class="kpi-value">${formatCurrency(eco.range_low || 240000000)} – ${formatCurrency(eco.range_high || 340000000)}</div>
            <div class="kpi-label">Estimated Brand Value Range</div>
            <div class="kpi-sublabel">Directional strategic indicator</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-value">${brand.overall_brand_score || 71}</div>
            <div class="kpi-label">Overall Brand Score</div>
            <div class="kpi-sublabel">Composite index (0-100)</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-value positive">+${eco.year_over_year_change || 18.5}%</div>
            <div class="kpi-label">YoY Growth</div>
            <div class="kpi-sublabel">vs. prior cycle</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-value">#1</div>
            <div class="kpi-label">CONCACAF Rank</div>
            <div class="kpi-sublabel">Confederation position</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-value positive">+$50M</div>
            <div class="kpi-label">2026 Impact</div>
            <div class="kpi-sublabel">Base case projection</div>
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Top 3 Strategic Risks</div>
        <div class="risk-grid">
          <div class="risk-card medium">
            <div class="risk-header">
              <div class="risk-title">Player Dependency Risk</div>
              <span class="risk-badge medium">MEDIUM</span>
            </div>
            <div class="risk-impact negative">-$15M to -$25M</div>
            <div class="risk-description">
              Christian Pulisic represents ~22% of aggregate roster brand value. Extended injury or form decline creates concentration exposure.
            </div>
            <div class="risk-recommendation">
              <strong>Recommendation:</strong>
              Accelerate next-generation player brand development. Diversify media exposure across top-5 roster players.
            </div>
          </div>
          
          <div class="risk-card high">
            <div class="risk-header">
              <div class="risk-title">Tournament Performance Risk</div>
              <span class="risk-badge high">HIGH</span>
            </div>
            <div class="risk-impact negative">-$40M to -$60M</div>
            <div class="risk-description">
              Group stage exit in 2026 as co-host would represent significant brand destruction. Historical precedent: Brazil 2014.
            </div>
            <div class="risk-recommendation">
              <strong>Recommendation:</strong>
              Implement performance contingency communications strategy. Prepare sponsor relationship management protocols.
            </div>
          </div>
          
          <div class="risk-card low">
            <div class="risk-header">
              <div class="risk-title">Media Sentiment Risk</div>
              <span class="risk-badge low">LOW</span>
            </div>
            <div class="risk-impact negative">-$5M to -$10M</div>
            <div class="risk-description">
              Current media sentiment index at 72/100 (positive). No significant controversies. Social engagement metrics stable.
            </div>
            <div class="risk-recommendation">
              <strong>Recommendation:</strong>
              Maintain proactive media relations. Continue athlete media training program.
            </div>
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Top 3 Strategic Opportunities</div>
        <div class="risk-grid">
          <div class="risk-card opp-card">
            <div class="risk-header">
              <div class="risk-title">Sponsorship Whitespace Opportunity</div>
              <span class="risk-badge low">12-18 MO</span>
            </div>
            <div class="risk-impact opp-impact">+$20M to +$35M</div>
            <div class="risk-description">
              Estimated sponsorship portfolio at ~78% of top European federation equivalents (modeled baseline). Potential for category depth optimization and new vertical expansion ahead of 2026 cycle.
            </div>
            <div class="risk-recommendation">
              <strong>Activation:</strong>
              Evaluate category depth potential and rate optimization across existing and prospective sponsor verticals ahead of 2026 cycle.
            </div>
          </div>
          
          <div class="risk-card opp-card">
            <div class="risk-header">
              <div class="risk-title">2026 Host Premium</div>
              <span class="risk-badge low">18-24 MO</span>
            </div>
            <div class="risk-impact opp-impact">+$50M to +$65M</div>
            <div class="risk-description">
              Co-host status provides unprecedented domestic media exposure. Historical data indicates 35-50% brand value increase.
            </div>
            <div class="risk-recommendation">
              <strong>Activation:</strong>
              Maximize owned media production. Launch 2026-specific merchandise licensing by Q2 2025.
            </div>
          </div>
          
          <div class="risk-card opp-card">
            <div class="risk-header">
              <div class="risk-title">Digital Acceleration</div>
              <span class="risk-badge low">6-12 MO</span>
            </div>
            <div class="risk-impact opp-impact">+$10M to +$18M</div>
            <div class="risk-description">
              Digital monetization at 42% of English FA benchmark. TikTok engagement velocity 2.3x YoY.
            </div>
            <div class="risk-recommendation">
              <strong>Activation:</strong>
              Implement D2C content subscription model. Evaluate blockchain fan engagement pilot.
            </div>
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">2026 Strategic Projection Summary</div>
        <p style="font-size: 10px; color: #666; margin-bottom: 10px;">
          Tournament outcome sensitivity analysis for FIFA World Cup 2026. Brand value impact projections based on historical host country performance.
        </p>
        <table class="projection-table">
          <thead>
            <tr>
              <th>Tournament Outcome</th>
              <th>Probability</th>
              <th>Brand Value Impact</th>
              <th>Assessment</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Group Stage Exit</strong></td>
              <td>10%</td>
              <td class="impact-negative">-$40M</td>
              <td>Brand value decline from unmet host expectations</td>
            </tr>
            <tr>
              <td><strong>Round of 32</strong></td>
              <td>15%</td>
              <td class="impact-neutral">+$10M</td>
              <td>Minimum knockout advancement; modest uplift</td>
            </tr>
            <tr>
              <td><strong>Round of 16</strong></td>
              <td>30%</td>
              <td class="impact-neutral">+$25M</td>
              <td>Meets baseline expectations for co-host</td>
            </tr>
            <tr>
              <td><strong>Quarterfinals</strong></td>
              <td>25%</td>
              <td class="impact-positive">+$50M</td>
              <td>Base case projection; meets market expectations</td>
            </tr>
            <tr>
              <td><strong>Semifinals</strong></td>
              <td>14%</td>
              <td class="impact-positive">+$125M</td>
              <td>Significant brand acceleration; historic achievement</td>
            </tr>
            <tr>
              <td><strong>Final / Champions</strong></td>
              <td>6%</td>
              <td class="impact-positive">+$250M</td>
              <td>Transformational brand value event</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="disclaimer">
        <strong>Important Disclaimer:</strong> The valuations, projections, and strategic assessments presented are 
        directional indicators derived from publicly available data and proprietary analytical models. They do not 
        constitute financial advice, audited appraisals, or certified valuations. All figures are subject to 
        variation based on market conditions, tournament outcomes, and other external factors. This document is 
        intended solely for internal strategic planning within the authorized recipient organization.
      </div>

      <div class="footer">
        <div><strong>YBV</strong> Brand Intelligence Platform</div>
        <div>ISO 10668 Aligned | BSI-Aligned Methodology | Interbrand-Aligned Valuation</div>
      </div>
      <div class="pdf-watermark-footer">CONFIDENTIAL — US SOCCER PILOT | Generated ${now.toISOString()}</div>
    </body>
    </html>
  `;
};

const generateNationalTeamReport = (data) => {
  const brand = data.brand_metrics || {};
  const digital = data.digital_presence || {};
  const eco = data.economic_value || {};
  const cycle = data.current_cycle || {};
  const roster = data.roster_brand_signals || {};
  
  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(0)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
  };
  
  const formatNumber = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value?.toString() || '0';
  };
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>YBV Brand Intelligence Report - ${data.name}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          color: #1a1a2e;
          line-height: 1.5;
          padding: 40px;
          max-width: 800px;
          margin: 0 auto;
          position: relative;
        }
        .pdf-watermark {
          position: fixed; top: 50%; left: 50%;
          transform: translate(-50%, -50%) rotate(-35deg);
          font-size: 48px; font-weight: 800; color: rgba(220, 38, 38, 0.06);
          white-space: nowrap; pointer-events: none; z-index: 0; letter-spacing: 4px;
        }
        .pdf-watermark-footer {
          text-align: center; font-size: 9px; color: #999;
          border-top: 1px solid #e5e7eb; padding-top: 12px; margin-top: 30px;
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          border-bottom: 2px solid #1a1a2e;
          padding-bottom: 20px;
          margin-bottom: 30px;
        }
        .logo { font-size: 24px; font-weight: bold; color: #22c55e; }
        .logo span { color: #1a1a2e; }
        .report-date { font-size: 12px; color: #666; }
        .team-name { font-size: 28px; font-weight: bold; margin-bottom: 5px; }
        .team-rank { 
          display: inline-block;
          background: #f0f0f0;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: bold;
        }
        .section { margin-bottom: 30px; }
        .section-title { 
          font-size: 16px;
          font-weight: bold;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #666;
          margin-bottom: 15px;
          border-bottom: 1px solid #eee;
          padding-bottom: 5px;
        }
        .value-large { 
          font-size: 36px;
          font-weight: bold;
          color: #22c55e;
        }
        .value-range { font-size: 14px; color: #666; }
        .grid { display: grid; gap: 15px; }
        .grid-2 { grid-template-columns: 1fr 1fr; }
        .grid-3 { grid-template-columns: 1fr 1fr 1fr; }
        .grid-5 { grid-template-columns: repeat(5, 1fr); }
        .metric-card {
          background: #f8f8f8;
          padding: 15px;
          border-radius: 8px;
          text-align: center;
        }
        .metric-value { font-size: 24px; font-weight: bold; color: #1a1a2e; }
        .metric-label { font-size: 11px; color: #666; text-transform: uppercase; }
        .table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .table th, .table td { 
          padding: 8px; 
          text-align: left; 
          border-bottom: 1px solid #eee; 
        }
        .table th { 
          background: #f0f0f0; 
          font-weight: 600; 
          text-transform: uppercase;
          font-size: 10px;
          letter-spacing: 0.5px;
        }
        .progress-bar {
          background: #e0e0e0;
          height: 8px;
          border-radius: 4px;
          overflow: hidden;
        }
        .progress-fill {
          background: #22c55e;
          height: 100%;
        }
        .disclaimer {
          font-size: 10px;
          color: #999;
          margin-top: 40px;
          padding-top: 20px;
          border-top: 1px solid #eee;
        }
        .footer {
          margin-top: 20px;
          text-align: center;
          font-size: 10px;
          color: #999;
        }
        @media print {
          body { padding: 20px; }
          .section { page-break-inside: avoid; }
        }
      </style>
    </head>
    <body>
      <div class="pdf-watermark">CONFIDENTIAL — US SOCCER PILOT</div>
      <div class="header">
        <div>
          <div class="logo"><span>YBV</span> Brand Intelligence</div>
          <div class="report-date">Generated: ${new Date().toLocaleDateString('en-US', { 
            year: 'numeric', month: 'long', day: 'numeric' 
          })}</div>
        </div>
        <div style="text-align: right;">
          <div class="team-name">${data.name}</div>
          <span class="team-rank">FIFA #${data.fifa_ranking}</span>
        </div>
      </div>
      
      <div class="section">
        <div class="section-title">Brand Valuation Summary</div>
        <div class="grid grid-2">
          <div>
            <div class="value-large">${formatCurrency(eco.range_low)} – ${formatCurrency(eco.range_high)}</div>
            <div class="value-range">Estimated Brand Value Range</div>
          </div>
          <div style="text-align: right;">
            <div style="font-size: 24px; font-weight: bold; color: #22c55e;">+${eco.year_over_year_change}%</div>
            <div class="value-range">Year-over-Year Change</div>
          </div>
        </div>
      </div>
      
      <div class="section">
        <div class="section-title">Brand Metrics</div>
        <div class="grid grid-5">
          <div class="metric-card">
            <div class="metric-value">${brand.overall_brand_score || 0}</div>
            <div class="metric-label">Overall Score</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">${brand.brand_strength_index || 0}</div>
            <div class="metric-label">Brand Strength</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">${brand.commercial_attractiveness || 0}</div>
            <div class="metric-label">Commercial</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">${brand.global_recognition || 0}</div>
            <div class="metric-label">Global</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">${brand.tournament_performance_factor || 0}</div>
            <div class="metric-label">Tournament</div>
          </div>
        </div>
      </div>
      
      <div class="section">
        <div class="section-title">World Cup Cycle Progress</div>
        <div style="margin-bottom: 10px;">
          <strong>${cycle.cycle_name || 'Current Cycle'}</strong>
          <span style="float: right;">${cycle.cycle_progress_pct || 0}%</span>
        </div>
        <div class="progress-bar">
          <div class="progress-fill" style="width: ${cycle.cycle_progress_pct || 0}%"></div>
        </div>
        <table class="table" style="margin-top: 15px;">
          <thead>
            <tr>
              <th>Tournament</th>
              <th>Year</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            ${(cycle.key_tournaments || []).map(t => `
              <tr>
                <td>${t.name}</td>
                <td>${t.year}</td>
                <td>${t.result}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
      
      <div class="section">
        <div class="section-title">Digital Presence</div>
        <div class="grid grid-2">
          <div class="grid grid-2">
            <div class="metric-card">
              <div class="metric-value">${formatNumber(digital.instagram?.followers)}</div>
              <div class="metric-label">Instagram</div>
            </div>
            <div class="metric-card">
              <div class="metric-value">${formatNumber(digital.twitter?.followers)}</div>
              <div class="metric-label">Twitter/X</div>
            </div>
            <div class="metric-card">
              <div class="metric-value">${formatNumber(digital.tiktok?.followers)}</div>
              <div class="metric-label">TikTok</div>
            </div>
            <div class="metric-card">
              <div class="metric-value">${formatNumber(digital.youtube?.subscribers)}</div>
              <div class="metric-label">YouTube</div>
            </div>
          </div>
          <div class="metric-card" style="display: flex; flex-direction: column; justify-content: center;">
            <div class="metric-value" style="color: #22c55e;">${formatNumber(digital.total_social_reach)}</div>
            <div class="metric-label">Total Social Reach</div>
          </div>
        </div>
      </div>
      
      <div class="section">
        <div class="section-title">Top Players by Brand Score</div>
        <table class="table">
          <thead>
            <tr>
              <th>#</th>
              <th>Player</th>
              <th>Instagram</th>
              <th>Brand Score</th>
            </tr>
          </thead>
          <tbody>
            ${(roster.star_players || []).slice(0, 5).map((p, i) => `
              <tr>
                <td>${i + 1}</td>
                <td>${p.name}</td>
                <td>${formatNumber(p.instagram_followers)}</td>
                <td><strong>${p.brand_score}</strong></td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
      
      <div class="section">
        <div class="section-title">Methodology Summary</div>
        <p style="font-size: 12px; color: #666; margin-bottom: 10px;">
          Brand valuations are calculated using a weighted composite of:
        </p>
        <ul style="font-size: 12px; color: #666; padding-left: 20px;">
          <li>Digital Presence (20%) - Social media reach and engagement</li>
          <li>Roster Brand Power (25%) - Aggregate player brand value</li>
          <li>Tournament Performance (20%) - World Cup cycle results</li>
          <li>Commercial Strength (20%) - Sponsorship portfolio</li>
          <li>Market Factors (15%) - Host premium, market size</li>
        </ul>
        <p style="font-size: 11px; color: #999; margin-top: 10px;">
          Aligned with Brand Finance BSI, Interbrand, and ISO 10668 published methodologies.
        </p>
      </div>
      
      <div class="disclaimer">
        <strong>Disclaimer:</strong> The valuations and projections presented are based on public data signals 
        and industry benchmarks. They do not constitute financial advice or official appraisals. 
        All figures are directional indicators for strategic planning purposes only.
      </div>
      
      <div class="footer">
        YBV Brand Intelligence Platform | Federation-Level Strategic Analytics
      </div>
      <div class="pdf-watermark-footer">CONFIDENTIAL — US SOCCER PILOT | Generated ${new Date().toISOString()}</div>
    </body>
    </html>
  `;
};

const generateAthleteReport = (data) => {
  const profile = data.profile || {};
  const metrics = data.metrics || profile.metrics || {};
  const social = data.social || {};
  const economic = data.economic || {};
  
  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
  };
  
  const formatNumber = (value) => {
    if (!value) return '0';
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value.toString();
  };
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>YBV Brand Intelligence Report - ${profile.name || 'Athlete'}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          color: #1a1a2e;
          line-height: 1.5;
          padding: 40px;
          max-width: 800px;
          margin: 0 auto;
          position: relative;
        }
        .pdf-watermark {
          position: fixed; top: 50%; left: 50%;
          transform: translate(-50%, -50%) rotate(-35deg);
          font-size: 48px; font-weight: 800; color: rgba(220, 38, 38, 0.06);
          white-space: nowrap; pointer-events: none; z-index: 0; letter-spacing: 4px;
        }
        .pdf-watermark-footer {
          text-align: center; font-size: 9px; color: #999;
          border-top: 1px solid #e5e7eb; padding-top: 12px; margin-top: 30px;
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          border-bottom: 2px solid #1a1a2e;
          padding-bottom: 20px;
          margin-bottom: 30px;
        }
        .logo { font-size: 24px; font-weight: bold; color: #22c55e; }
        .logo span { color: #1a1a2e; }
        .report-date { font-size: 12px; color: #666; }
        .profile-name { font-size: 28px; font-weight: bold; margin-bottom: 5px; }
        .profile-role { font-size: 14px; color: #666; }
        .section { margin-bottom: 30px; }
        .section-title { 
          font-size: 16px;
          font-weight: bold;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #666;
          margin-bottom: 15px;
          border-bottom: 1px solid #eee;
          padding-bottom: 5px;
        }
        .value-large { 
          font-size: 36px;
          font-weight: bold;
          color: #22c55e;
        }
        .value-range { font-size: 14px; color: #666; }
        .grid { display: grid; gap: 15px; }
        .grid-2 { grid-template-columns: 1fr 1fr; }
        .grid-4 { grid-template-columns: repeat(4, 1fr); }
        .metric-card {
          background: #f8f8f8;
          padding: 15px;
          border-radius: 8px;
          text-align: center;
        }
        .metric-value { font-size: 24px; font-weight: bold; color: #1a1a2e; }
        .metric-label { font-size: 11px; color: #666; text-transform: uppercase; }
        .disclaimer {
          font-size: 10px;
          color: #999;
          margin-top: 40px;
          padding-top: 20px;
          border-top: 1px solid #eee;
        }
        .footer {
          margin-top: 20px;
          text-align: center;
          font-size: 10px;
          color: #999;
        }
        @media print {
          body { padding: 20px; }
          .section { page-break-inside: avoid; }
        }
      </style>
    </head>
    <body>
      <div class="pdf-watermark">CONFIDENTIAL — US SOCCER PILOT</div>
      <div class="header">
        <div>
          <div class="logo"><span>YBV</span> Brand Intelligence</div>
          <div class="report-date">Generated: ${new Date().toLocaleDateString('en-US', { 
            year: 'numeric', month: 'long', day: 'numeric' 
          })}</div>
        </div>
        <div style="text-align: right;">
          <div class="profile-name">${profile.name || 'Profile'}</div>
          <div class="profile-role">${profile.role || ''} | ${profile.team || ''}</div>
        </div>
      </div>
      
      <div class="section">
        <div class="section-title">Brand Value Summary</div>
        <div class="grid grid-2">
          <div>
            <div class="value-large">${formatCurrency(economic?.range_low)} – ${formatCurrency(economic?.range_high)}</div>
            <div class="value-range">Estimated Brand Value Range</div>
          </div>
          <div style="text-align: right;">
            <div style="font-size: 24px; font-weight: bold;">${metrics?.overallIndex?.score || metrics?.brandStrength?.score || 'N/A'}</div>
            <div class="value-range">Overall Brand Score</div>
          </div>
        </div>
      </div>
      
      <div class="section">
        <div class="section-title">Core Metrics</div>
        <div class="grid grid-4">
          <div class="metric-card">
            <div class="metric-value">${metrics?.brandStrength?.score || 0}</div>
            <div class="metric-label">Brand Strength</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">${metrics?.commercialAttractiveness?.score || 0}</div>
            <div class="metric-label">Commercial</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">${metrics?.reputationStability?.score || 0}</div>
            <div class="metric-label">Reputation</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">${metrics?.riskExposure?.score || 0}</div>
            <div class="metric-label">Risk Exposure</div>
          </div>
        </div>
      </div>
      
      <div class="section">
        <div class="section-title">Digital Presence</div>
        <div class="grid grid-4">
          <div class="metric-card">
            <div class="metric-value">${formatNumber(social?.instagram?.followers)}</div>
            <div class="metric-label">Instagram</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">${formatNumber(social?.twitter?.followers)}</div>
            <div class="metric-label">Twitter/X</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">${formatNumber(social?.linkedin?.connections)}</div>
            <div class="metric-label">LinkedIn</div>
          </div>
          <div class="metric-card">
            <div class="metric-value" style="color: #22c55e;">${formatNumber(social?.total)}</div>
            <div class="metric-label">Total Reach</div>
          </div>
        </div>
      </div>
      
      <div class="disclaimer">
        <strong>Disclaimer:</strong> The valuations and projections presented are based on public data signals 
        and industry benchmarks. They do not constitute financial advice or official appraisals. 
        All figures are directional indicators for strategic planning purposes only.
      </div>
      
      <div class="footer">
        YBV Brand Intelligence Platform | Strategic Analytics
      </div>
      <div class="pdf-watermark-footer">CONFIDENTIAL — US SOCCER PILOT | Generated ${new Date().toISOString()}</div>
    </body>
    </html>
  `;
};

export default { generatePDFReport };